import { useState, useEffect, useRef } from 'react';
import { Users, MessageSquare, Settings, TrendingUp, ChevronDown, ChevronUp, Mail, MessageCircle, Pause, Archive, Play, Plus, Pencil, Trash2, X, FolderOpen, FileUp, Download, Eye, EyeOff, FileText, Loader2, ClipboardCheck, Ticket, Copy, Check, Brain, BookOpen, UserPlus, Activity, BarChart3, Clock } from 'lucide-react';
import { adminApi, categoryApi, materialApi, assessmentApi, couponApi } from '../services/api';
import toast from 'react-hot-toast';

const getWhatsAppUrl = (phone) => {
  if (!phone) return null;
  let cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('0')) cleaned = '91' + cleaned.slice(1);
  if (cleaned.length === 10) cleaned = '91' + cleaned;
  return `https://wa.me/${cleaned}`;
};

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashRes, configRes] = await Promise.all([
        adminApi.getDashboard(),
        adminApi.getConfig()
      ]);
      setStats(dashRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: TrendingUp },
    { id: 'students', name: 'Students', icon: Users },
    { id: 'categories', name: 'Exam Categories', icon: FolderOpen },
    { id: 'materials', name: 'Study Materials', icon: FileUp },
    { id: 'mocktests', name: 'Mock Tests', icon: ClipboardCheck },
    { id: 'coupons', name: 'Coupons', icon: Ticket },
    { id: 'settings', name: 'Settings', icon: Settings },
  ];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">ExamPrep Admin Dashboard</h1>

      {/* Tabs */}
      <div className="flex space-x-1 border-b mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {tab.name}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab stats={stats} />}
      {activeTab === 'students' && <StudentsTab />}
      {activeTab === 'categories' && <ExamCategoriesTab />}
      {activeTab === 'materials' && <MaterialsTab />}
      {activeTab === 'mocktests' && <MockTestsTab />}
      {activeTab === 'coupons' && <CouponsTab />}
      {activeTab === 'settings' && <SettingsTab config={config} onSave={loadData} />}
    </div>
  );
}

function OverviewTab({ stats }) {
  const statCards = [
    { label: 'Total Sign-ups', value: stats?.totalStudents?.count || 0, icon: Users, color: 'blue', sub: `+${stats?.signupsToday?.count || 0} today, +${stats?.signupsWeek?.count || 0} this week` },
    { label: 'Active Today', value: stats?.activeUsersToday?.count || 0, icon: Activity, color: 'green', sub: 'using platform today' },
    { label: 'Active (7 days)', value: stats?.activeUsers7d?.count || 0, icon: Activity, color: 'teal', sub: 'last 7 days' },
    { label: 'Mock Tests', value: stats?.totalMockTests?.count || 0, icon: ClipboardCheck, color: 'purple' },
    { label: 'Study Materials', value: stats?.totalMaterials?.count || 0, icon: BookOpen, color: 'indigo' },
    { label: 'AI Doubts Asked', value: stats?.totalDoubts?.count || 0, icon: Brain, color: 'orange' },
  ];

  const colorMap = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    teal: 'bg-teal-50 text-teal-600',
    purple: 'bg-purple-50 text-purple-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    orange: 'bg-orange-50 text-orange-600',
    pink: 'bg-pink-50 text-pink-600',
  };

  const maxExamCount = Math.max(...(stats?.popularExams || []).map(e => e.count), 1);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <div key={i} className="bg-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-shadow">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colorMap[card.color]}`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{card.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{card.label}</p>
              {card.sub && <p className="text-[10px] text-gray-500 mt-1">{card.sub}</p>}
            </div>
          );
        })}
      </div>

      {/* Middle Row: Popular Exams + Daily Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popular Exams */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center"><BarChart3 className="w-4 h-4 mr-2 text-purple-500" />Popular Exams</h3>
            <span className="text-xs text-gray-400">by mock tests generated</span>
          </div>
          {(stats?.popularExams || []).length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No mock tests generated yet</p>
          ) : (
            <div className="space-y-3">
              {(stats?.popularExams || []).map((exam, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700 w-24 truncate">{exam.exam_name}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-end pr-2 transition-all" style={{ width: `${Math.max((exam.count / maxExamCount) * 100, 12)}%` }}>
                      <span className="text-[10px] font-bold text-white">{exam.count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Daily Activity Chart (CSS-only bar chart) */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center"><Activity className="w-4 h-4 mr-2 text-green-500" />Daily Activity (14 days)</h3>
            <span className="text-xs text-gray-400">active users & questions</span>
          </div>
          {(stats?.dailyActivity || []).length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No activity data yet</p>
          ) : (
            <div>
              <div className="flex items-end gap-1 h-32">
                {(stats?.dailyActivity || []).map((day, i) => {
                  const maxQ = Math.max(...(stats?.dailyActivity || []).map(d => d.questions || 0), 1);
                  const height = Math.max(((day.questions || 0) / maxQ) * 100, 4);
                  const dateStr = new Date(day.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end group relative">
                      <div className="absolute -top-8 bg-gray-800 text-white text-[9px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                        {dateStr}: {day.active_users} users, {day.questions || 0} Q
                      </div>
                      <div className="w-full bg-gradient-to-t from-green-500 to-emerald-400 rounded-t transition-all hover:from-green-600 hover:to-emerald-500" style={{ height: `${height}%` }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[9px] text-gray-400">{(stats?.dailyActivity || [])[0]?.date ? new Date(stats.dailyActivity[0].date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}</span>
                <span className="text-[9px] text-gray-400">Today</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row: Recent Mock Tests + Recent Sign-ups */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Mock Tests */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b bg-gray-50 flex items-center">
            <ClipboardCheck className="w-4 h-4 mr-2 text-purple-500" />
            <h3 className="font-semibold text-gray-700">Recent Mock Tests</h3>
          </div>
          {(stats?.recentMockTests || []).length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No mock tests yet</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {(stats?.recentMockTests || []).map((test, i) => (
                <div key={i} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{test.title}</p>
                    <p className="text-xs text-gray-500">{test.student_name || 'Unknown'} &middot; {test.question_count || '?'} questions</p>
                  </div>
                  <span className="text-xs text-gray-400 flex items-center whitespace-nowrap ml-3">
                    <Clock className="w-3 h-3 mr-1" />
                    {new Date(test.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Sign-ups */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b bg-gray-50 flex items-center">
            <UserPlus className="w-4 h-4 mr-2 text-blue-500" />
            <h3 className="font-semibold text-gray-700">Recent Sign-ups</h3>
          </div>
          {(stats?.recentSignups || []).length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No students registered yet</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {(stats?.recentSignups || []).map((student, i) => (
                <div key={i} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-sm font-semibold text-blue-600">{student.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{student.name}</p>
                      <p className="text-xs text-gray-500">{student.email}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {new Date(student.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StudentsTab() {
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadStudents();
  }, []);

  const loadStudents = async () => {
    try {
      const res = await adminApi.getStudents({ limit: 50 });
      setStudents(res.data);
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddQuestions = async (student) => {
    const input = prompt(`Add questions for ${student.name}\nCurrent: ${student.available_questions ?? 0}\n\nEnter number of questions to add:`);
    if (!input) return;
    const questions = parseInt(input);
    if (isNaN(questions) || questions < 1) {
      toast.error('Enter a valid number');
      return;
    }
    try {
      const res = await adminApi.addStudentQuestions(student.id, questions);
      toast.success(res.data.message);
      loadStudents();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add questions');
    }
  };

  const handleStudentStatus = async (studentId, status) => {
    const label = status === 'paused' ? 'Pause' : status === 'archived' ? 'Pause & Archive' : 'Reactivate';
    if (!confirm(`${label} this student?`)) return;
    try {
      await adminApi.updateStudentStatus(studentId, status);
      toast.success(`Student ${status === 'active' ? 'reactivated' : status}`);
      loadStudents();
    } catch (error) {
      toast.error('Failed to update student status');
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {students.map((student) => (
            <tr key={student.id}>
              <td className="px-4 py-3 text-sm font-medium text-gray-900">{student.name}</td>
              <td className="px-4 py-3 text-sm text-gray-600">{student.email}</td>
              <td className="px-4 py-3 text-sm text-gray-600">{student.available_questions}</td>
              <td className="px-4 py-3">
                <span className={`text-xs px-2 py-1 rounded-full ${
                  student.status === 'paused' ? 'bg-amber-100 text-amber-700' :
                  student.status === 'archived' ? 'bg-red-100 text-red-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {student.status === 'paused' ? 'Paused' :
                   student.status === 'archived' ? 'Archived' : 'Active'}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleAddQuestions(student)}
                    title="Add Questions"
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                  {(!student.status || student.status === 'active') && (
                    <>
                      <button
                        onClick={() => handleStudentStatus(student.id, 'paused')}
                        title="Pause"
                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                      >
                        <Pause className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleStudentStatus(student.id, 'archived')}
                        title="Pause & Archive"
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Archive className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  {(student.status === 'paused' || student.status === 'archived') && (
                    <button
                      onClick={() => handleStudentStatus(student.id, 'active')}
                      title="Reactivate"
                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    >
                      <Play className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <a
                    href={`mailto:${student.email}`}
                    title="Send Email"
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                  </a>
                  {student.phone && (
                    <a
                      href={getWhatsAppUrl(student.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="WhatsApp"
                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExamCategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '', description: '', icon: '' });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const res = await categoryApi.listAll();
      setCategories(res.data);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateForm = () => {
    setEditingCategory(null);
    setFormData({ name: '', slug: '', description: '', icon: '' });
    setShowForm(true);
  };

  const openEditForm = (cat) => {
    setEditingCategory(cat);
    setFormData({
      name: cat.name || '',
      slug: cat.slug || '',
      description: cat.description || '',
      icon: cat.icon || ''
    });
    setShowForm(true);
  };

  const handleNameChange = (name) => {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    setFormData(prev => ({
      ...prev,
      name,
      slug: editingCategory ? prev.slug : slug
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.slug.trim()) {
      toast.error('Name and slug are required');
      return;
    }
    setIsSaving(true);
    try {
      if (editingCategory) {
        await categoryApi.update(editingCategory.id, formData);
        toast.success('Category updated');
      } else {
        await categoryApi.create(formData);
        toast.success('Category created');
      }
      setShowForm(false);
      setEditingCategory(null);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save category');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeactivate = async (catId) => {
    if (!confirm('Deactivate this category?')) return;
    try {
      await categoryApi.delete(catId);
      toast.success('Category deactivated');
      loadCategories();
    } catch (error) {
      toast.error('Failed to deactivate category');
    }
  };

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Exam Categories</h2>
        <button
          onClick={openCreateForm}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </button>
      </div>

      {/* Create / Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-primary-200 p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">{editingCategory ? 'Edit Category' : 'New Category'}</h3>
            <button onClick={() => { setShowForm(false); setEditingCategory(null); }} className="p-1 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g., CAT Exam"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                <input
                  type="text"
                  required
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="e.g., cat-exam"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this exam category"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Icon (emoji or icon name)</label>
              <input
                type="text"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder="e.g., pencil or an emoji"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button type="button" onClick={() => { setShowForm(false); setEditingCategory(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">
                Cancel
              </button>
              <button type="submit" disabled={isSaving} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm disabled:opacity-50">
                {isSaving ? 'Saving...' : (editingCategory ? 'Update Category' : 'Create Category')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Categories Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Icon</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {categories.map((cat) => (
              <tr key={cat.id}>
                <td className="px-4 py-3 text-lg">{cat.icon || '—'}</td>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{cat.name}</td>
                <td className="px-4 py-3 text-sm text-gray-500 font-mono">{cat.slug}</td>
                <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{cat.description || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    cat.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {cat.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => openEditForm(cat)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {cat.is_active !== false && (
                      <button
                        onClick={() => handleDeactivate(cat.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Deactivate"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {categories.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No exam categories yet. Click "Add Category" to create one.
          </div>
        )}
      </div>
    </div>
  );
}

function SettingsTab({ config, onSave }) {
  const [formData, setFormData] = useState(config);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await adminApi.updateConfig(formData);
      toast.success('Settings saved');
      onSave();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-xl">
      <h2 className="text-lg font-semibold mb-4">Platform Configuration</h2>

      <div className="space-y-4">
        <p className="text-sm text-gray-500">Platform settings will appear here as they are configured.</p>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

function MaterialsTab() {
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Upload form
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { loadMaterials(); loadCategories(); }, []);

  const loadMaterials = async () => {
    try {
      const params = filterCat ? { category: filterCat } : {};
      const res = await materialApi.list(params);
      setMaterials(res.data || []);
    } catch (error) { toast.error('Failed to load materials'); }
    finally { setIsLoading(false); }
  };

  const loadCategories = async () => {
    try {
      const res = await categoryApi.list();
      setCategories(res.data || []);
    } catch (error) { /* ignore */ }
  };

  useEffect(() => { loadMaterials(); }, [filterCat]);

  const handleUpload = async () => {
    if (!file) { toast.error('Please select a file'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title || file.name);
      if (description) formData.append('description', description);
      if (categoryId) formData.append('category_id', categoryId);

      await materialApi.upload(formData);
      toast.success('Material uploaded!');
      setShowUpload(false);
      setTitle(''); setDescription(''); setCategoryId(''); setFile(null);
      loadMaterials();
    } catch (error) { toast.error(error.response?.data?.error || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this material?')) return;
    try {
      await materialApi.delete(id);
      setMaterials(prev => prev.filter(m => m.id !== id));
      toast.success('Deleted');
    } catch (error) { toast.error('Failed to delete'); }
  };

  const handleTogglePublish = async (id, current) => {
    try {
      await materialApi.update(id, { is_published: !current });
      setMaterials(prev => prev.map(m => m.id === id ? { ...m, is_published: !current } : m));
      toast.success(!current ? 'Published' : 'Unpublished');
    } catch (error) { toast.error('Failed to update'); }
  };

  const handleEditSave = async (id) => {
    try {
      await materialApi.update(id, editForm);
      setMaterials(prev => prev.map(m => m.id === id ? { ...m, ...editForm } : m));
      setEditingId(null);
      toast.success('Updated');
    } catch (error) { toast.error('Failed to update'); }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const fileIcon = (type) => {
    const icons = { pdf: '📄', doc: '📝', docx: '📝', ppt: '📊', pptx: '📊', xls: '📈', xlsx: '📈', txt: '📃', md: '📃', jpg: '🖼️', jpeg: '🖼️', png: '🖼️' };
    return icons[type] || '📎';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Study Materials</h2>
        <button onClick={() => setShowUpload(!showUpload)} className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm">
          <Plus className="w-4 h-4 mr-1" /> Upload Material
        </button>
      </div>

      {/* Upload Form */}
      {showUpload && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
          <h3 className="font-semibold text-gray-900 mb-3">Upload New Material</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Title (optional - uses filename)" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500" />
            <select value={categoryId} onChange={e => setCategoryId(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500">
              <option value="">All Categories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)" rows={2} className="w-full mt-3 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500" />
          <div className="mt-3">
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files[0])} className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" />
            <p className="text-xs text-gray-400 mt-1">PDF, DOC, PPT, XLS, TXT, MD, JPG, PNG (max 50MB)</p>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={handleUpload} disabled={uploading || !file} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm disabled:opacity-50 flex items-center">
              {uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading...</> : <><FileUp className="w-4 h-4 mr-1" /> Upload</>}
            </button>
            <button onClick={() => { setShowUpload(false); setFile(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="mb-4">
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
        </select>
      </div>

      {/* Materials Table */}
      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : materials.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No materials uploaded yet</p>
          <button onClick={() => setShowUpload(true)} className="mt-3 text-sm text-primary-600 hover:text-primary-700">Upload your first material</button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Downloads</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {materials.map(m => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="text-lg">{fileIcon(m.file_type)}</span>
                    <span className="text-xs text-gray-400 ml-1 uppercase">{m.file_type}</span>
                  </td>
                  <td className="px-4 py-3">
                    {editingId === m.id ? (
                      <input value={editForm.title || ''} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} className="px-2 py-1 border rounded text-sm w-full" />
                    ) : (
                      <div>
                        <p className="font-medium text-gray-900">{m.title}</p>
                        {m.description && <p className="text-xs text-gray-500 truncate max-w-xs">{m.description}</p>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full">{m.category_name || 'General'}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{formatSize(m.file_size)}</td>
                  <td className="px-4 py-3">
                    <span className="flex items-center text-gray-500"><Download className="w-3 h-3 mr-1" /> {m.download_count}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleTogglePublish(m.id, m.is_published)} className={`flex items-center text-xs px-2 py-1 rounded-full ${m.is_published ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {m.is_published ? <><Eye className="w-3 h-3 mr-1" /> Published</> : <><EyeOff className="w-3 h-3 mr-1" /> Draft</>}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {editingId === m.id ? (
                        <>
                          <button onClick={() => handleEditSave(m.id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded">Save</button>
                          <button onClick={() => setEditingId(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded">Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => { setEditingId(m.id); setEditForm({ title: m.title, description: m.description }); }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(m.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MockTestsTab() {
  const [assessments, setAssessments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await assessmentApi.getAssessments();
      setAssessments(res.data || []);
    } catch (error) { /* ignore */ }
    finally { setIsLoading(false); }
  };

  const handleTogglePublish = async (id, current) => {
    try {
      await assessmentApi.updateAssessment(id, { is_published: !current });
      setAssessments(prev => prev.map(a => a.id === id ? { ...a, is_published: !current } : a));
      toast.success(!current ? 'Published!' : 'Unpublished');
    } catch (error) { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this mock test?')) return;
    try {
      await assessmentApi.deleteAssessment(id);
      setAssessments(prev => prev.filter(a => a.id !== id));
      toast.success('Deleted');
    } catch (error) { toast.error('Failed to delete'); }
  };

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Mock Tests ({assessments.length})</h2>
        <p className="text-sm text-gray-500">Students generate PYQ tests from the Mock Tests page</p>
      </div>

      {assessments.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <ClipboardCheck className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No mock tests yet</p>
          <p className="text-sm text-gray-400 mt-1">Tests will appear here when students generate PYQ papers</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {assessments.map(a => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{a.title}</p>
                    {a.description && <p className="text-xs text-gray-500 truncate max-w-xs">{a.description}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded-full">{a.category_name || 'General'}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{a.question_count}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {a.time_limit_minutes ? `${a.time_limit_minutes} min` : 'No limit'}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleTogglePublish(a.id, a.is_published)} className={`flex items-center text-xs px-2 py-1 rounded-full ${a.is_published ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {a.is_published ? <><Eye className="w-3 h-3 mr-1" /> Published</> : <><EyeOff className="w-3 h-3 mr-1" /> Draft</>}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleDelete(a.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    max_uses: '',
    min_amount: '',
    valid_from: '',
    valid_until: ''
  });

  useEffect(() => { loadCoupons(); }, []);

  const loadCoupons = async () => {
    try {
      const res = await couponApi.list();
      setCoupons(res.data || []);
    } catch (error) { toast.error('Failed to load coupons'); }
    finally { setIsLoading(false); }
  };

  const resetForm = () => {
    setFormData({ code: '', description: '', discount_type: 'percentage', discount_value: '', max_uses: '', min_amount: '', valid_from: '', valid_until: '' });
    setEditingCoupon(null);
    setShowForm(false);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditingCoupon(c);
    setFormData({
      code: c.code || '',
      description: c.description || '',
      discount_type: c.discount_type || 'percentage',
      discount_value: c.discount_value || '',
      max_uses: c.max_uses || '',
      min_amount: c.min_amount || '',
      valid_from: c.valid_from ? c.valid_from.slice(0, 16) : '',
      valid_until: c.valid_until ? c.valid_until.slice(0, 16) : ''
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code.trim() || !formData.discount_value) {
      toast.error('Code and discount value are required');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        min_amount: formData.min_amount ? parseFloat(formData.min_amount) : 0,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null
      };
      if (editingCoupon) {
        await couponApi.update(editingCoupon.id, payload);
        toast.success('Coupon updated');
      } else {
        await couponApi.create(payload);
        toast.success('Coupon created');
      }
      resetForm();
      loadCoupons();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save coupon');
    } finally { setIsSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this coupon?')) return;
    try {
      await couponApi.delete(id);
      setCoupons(prev => prev.filter(c => c.id !== id));
      toast.success('Coupon deleted');
    } catch (error) { toast.error('Failed to delete coupon'); }
  };

  const handleToggleActive = async (coupon) => {
    try {
      await couponApi.update(coupon.id, { is_active: !coupon.is_active });
      setCoupons(prev => prev.map(c => c.id === coupon.id ? { ...c, is_active: !c.is_active } : c));
      toast.success(coupon.is_active ? 'Coupon deactivated' : 'Coupon activated');
    } catch (error) { toast.error('Failed to update coupon'); }
  };

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatus = (c) => {
    if (!c.is_active) return { label: 'Inactive', cls: 'bg-gray-100 text-gray-600' };
    const now = new Date();
    if (c.valid_until && new Date(c.valid_until) < now) return { label: 'Expired', cls: 'bg-red-100 text-red-700' };
    if (c.valid_from && new Date(c.valid_from) > now) return { label: 'Scheduled', cls: 'bg-blue-100 text-blue-700' };
    if (c.max_uses && c.used_count >= c.max_uses) return { label: 'Exhausted', cls: 'bg-amber-100 text-amber-700' };
    return { label: 'Active', cls: 'bg-green-100 text-green-700' };
  };

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Coupons ({coupons.length})</h2>
        <button onClick={openCreate} className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm">
          <Plus className="w-4 h-4 mr-1" /> Create Coupon
        </button>
      </div>

      {/* Create / Edit Form */}
      {showForm && (
        <div className="bg-white border border-primary-200 rounded-xl p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">{editingCoupon ? 'Edit Coupon' : 'New Coupon'}</h3>
            <button onClick={resetForm} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., EXAM20"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono uppercase"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
                <select
                  value={formData.discount_type}
                  onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (Rs.)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Value {formData.discount_type === 'percentage' ? '(%)' : '(Rs.)'}
                </label>
                <input
                  type="number"
                  required
                  min="0.01"
                  max={formData.discount_type === 'percentage' ? '100' : undefined}
                  step="0.01"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                  placeholder={formData.discount_type === 'percentage' ? 'e.g., 20' : 'e.g., 500'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., 20% off on annual plan"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses (empty = unlimited)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  placeholder="Unlimited"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
                <input
                  type="datetime-local"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
                <input
                  type="datetime-local"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-1">
              <button type="button" onClick={resetForm} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={isSaving} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm disabled:opacity-50">
                {isSaving ? 'Saving...' : (editingCoupon ? 'Update Coupon' : 'Create Coupon')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Coupons Table */}
      {coupons.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Ticket className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No coupons yet</p>
          <button onClick={openCreate} className="mt-3 text-sm text-primary-600 hover:text-primary-700">Create your first coupon</button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valid Period</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {coupons.map(c => {
                const status = getStatus(c);
                return (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">{c.code}</span>
                        <button onClick={() => copyCode(c.code, c.id)} className="p-1 text-gray-400 hover:text-gray-600" title="Copy code">
                          {copiedId === c.id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      {c.description && <p className="text-xs text-gray-500 mt-0.5">{c.description}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-gray-900">
                        {c.discount_type === 'percentage' ? `${c.discount_value}%` : `Rs. ${c.discount_value}`}
                      </span>
                      <span className="text-xs text-gray-400 ml-1">off</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-900">{c.used_count}</span>
                      <span className="text-gray-400"> / {c.max_uses || '\u221E'}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      <div>{formatDate(c.valid_from)} - {formatDate(c.valid_until)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${status.cls}`}>{status.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(c)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleToggleActive(c)} className={`p-1.5 rounded ${c.is_active ? 'text-gray-400 hover:text-amber-600 hover:bg-amber-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`} title={c.is_active ? 'Deactivate' : 'Activate'}>
                          {c.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button onClick={() => handleDelete(c.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
